export enum UserDisplayStatus {
  NotInvited = 'NOT_INVITED',
  Invited = 'INVITED',
  Active = 'ACTIVE',
}

export enum InvitationStatus {
  Pending = 'PENDING',
  Used = 'USED',
  Expired = 'EXPIRED',
  Revoked = 'REVOKED',
}

export enum InvitationAuditEvent {
  UserInvited = 'USER_INVITED',
  InvitationResent = 'INVITATION_RESENT',
  InvitationRevoked = 'INVITATION_REVOKED',
  InvitationActivated = 'INVITATION_ACTIVATED',
  UserActivated = 'USER_ACTIVATED',
  RbacRolesUpdated = 'RBAC_ROLES_UPDATED',
  CompaniesSynced = 'COMPANIES_SYNCED',
  CompaniesRevoked = 'COMPANIES_REVOKED',
}

export type UserListSortField =
  | 'email'
  /** The company name the row's cell leads with; no company sorts last. */
  | 'companies'
  | 'createdAt'
  | 'activatedAt'
  | 'lastAccessAt'
  | 'lastInvoiceAccessAt'
  | 'lastInvoiceDownloadAt'
export type SortDirection = 'asc' | 'desc'

/** A resolved RBAC role for display in the user list. */
export interface UserRbacRole {
  id: string
  name: string
}

/**
 * A role option offered by the invite/assign dropdown, served by
 * GET /users/invite-roles (active invite-allowed roles; Finance Manager only in
 * v1). Lighter than the Access Control role shape by design.
 */
export interface InviteRole {
  id: string
  name: string
  status: string
}

export interface UserListRow {
  email: string
  /** Person's name from the persons mirror, null when we hold no record. */
  fullName: string | null
  userId: number | null
  personId: string | null
  /**
   * Assigned RBAC roles. Granted roles for ACTIVE users; pending roles (applied
   * on activation) for INVITED users.
   */
  rbacRoles: UserRbacRole[]
  /**
   * The RBAC role the user's Cognito account role implies, sent whether or not
   * they hold roles of their own. It only takes effect while `rbacRoles` is
   * empty, so it reads as access today for a user with no roles, and as what
   * removing their last role would leave behind for anyone else.
   */
  impliedRbacRoleIfUnassigned?: UserRbacRole | null
  displayStatus: UserDisplayStatus
  invitationStatus: InvitationStatus | null
  /** Companies whose active billing profile lists this person. */
  billingClientPublicKeys: string[]
  /** Display company names, index-aligned with billingClientPublicKeys. */
  billingClientNames: string[]
  createdAt: string | null
  /** When the invited user first signed in. Null while the invite is pending. */
  activatedAt: string | null
  /**
   * Last employer-portal access: stamped on password sign-in and on each silent
   * token refresh, so it tracks real usage rather than the last time a password
   * was typed. Null when they have never accessed the portal, or no sign-in
   * account resolved for this row.
   *
   * Employer portal only — admin-portal activity authenticates on a different
   * path and does not stamp this.
   */
  lastAccessAt: string | null
  /**
   * Last time this user opened the funding-invoice list. Null until they do —
   * there is no backfill, so expect this to be sparse right after release.
   */
  lastInvoiceAccessAt: string | null
  /**
   * Last time this user downloaded a funding invoice, from either portal.
   * Null until they do — there is no backfill.
   */
  lastInvoiceDownloadAt: string | null
  /**
   * True when the person is not active at the source of truth (terminated in
   * DynamoDB or not enabled+CONFIRMED in Cognito). Granting access skips these
   * rows. Absent/false means grantable.
   */
  isDeactivated?: boolean
}

/**
 * Whether a user has finished getting access: COMPLETE means an active account
 * holding a grantable role, INCOMPLETE is everyone short of that. An invitee
 * whose role is still pending is incomplete, since they have no way in yet.
 */
export type UserCompleteness = 'COMPLETE' | 'INCOMPLETE'

/**
 * A way of the role and the billing companies not lining up. Each bucket is a
 * display status plus, for the two invited cases, an invitation status, so they
 * cannot be asked for as two independent filters. The last two read further:
 * NEEDS_GRANT is an account that works but carries no grantable role, and
 * NO_COMPANIES is the reverse, someone holding the role who bills for nobody.
 */
export type UserStatusBucket =
  | 'INVITED'
  | 'EXPIRED'
  | 'NEEDS_GRANT'
  | 'NOT_INVITED'
  | 'NO_COMPANIES'

export interface GetUsersRequestDto {
  /** Matches the billing-contact email or the person's name. */
  search?: string
  page?: number
  limit?: number
  excludeTestClients?: boolean
  /** Status buckets; a user is kept when they match any of them. */
  statuses?: UserStatusBucket[]
  /** Keep users who are a billing contact for any of these companies. */
  clientPublicKeys?: string[]
  /**
   * Keeps only these addresses, matched case-insensitively. For asking about a
   * known handful: an address with no row comes back absent, which is the
   * answer that nothing is held for them.
   */
  emails?: string[]
  /**
   * Filters on the HR Admin column: true keeps the users it answers yes for,
   * false keeps the rest. Undefined leaves the column unfiltered. Yes includes
   * the role a user's Cognito role implies while they hold no roles.
   */
  hasHrAdminAccess?: boolean
  /** Same as hasHrAdminAccess, for the EE (Employee) column. */
  hasEmployeeAccess?: boolean
  sortBy?: UserListSortField
  sortDir?: SortDirection
  /** Narrows to users who have finished getting access, or to the rest. */
  completeness?: UserCompleteness
}

/**
 * How many users each filter option would return. Counted over the set matching
 * every other active filter but not the status or completeness choice itself, so
 * each number answers "how many rows if I pick this".
 */
export interface UserListStats {
  total: number
  active: number
  invited: number
  expired: number
  revoked: number
  notInvited: number
  /** Working accounts that bill for a company but carry no grantable role. */
  needsGrant: number
  /**
   * Role holders no active billing profile lists. They receive nothing and are
   * invisible to every other count, which is the whole reason to report them.
   */
  noCompanies: number
  /**
   * Users who have finished getting access. The incomplete count is not sent:
   * it is `total - complete`, so the two can never drift apart.
   */
  complete: number
}

export interface GetUsersResponseDto {
  data: UserListRow[]
  total: number
  page: number
  limit: number
  stats: UserListStats
}

/** The same filters as the list, without paging: the caller wants every match. */
export type GetAllUsersRequestDto = Omit<GetUsersRequestDto, 'page' | 'limit'>

export interface GetAllUsersResponseDto {
  data: UserListRow[]
  /**
   * Rows matching the filters. Greater than `data.length` when the server's cap
   * truncated the result, which has to be said out loud rather than passed off
   * as the whole set.
   */
  total: number
}

/**
 * One recipient of a bulk grant. The caller never says which action to take:
 * the server classifies each item against live account state and either
 * invites, resends, grants roles, or skips it with a reason.
 */
export interface BulkGrantAccessItemDto {
  email: string
  /** Hint only; the server re-resolves and verifies live state. */
  personId: string | null
  clientPublicKeys: string[]
  roleIds: string[]
}

export interface BulkGrantAccessRequestDto {
  grants: BulkGrantAccessItemDto[]
}

export enum BulkGrantAction {
  Invited = 'INVITED',
  /** Had a live invitation, so it was re-issued with the selected roles. */
  InvitationResent = 'INVITATION_RESENT',
  RoleAssigned = 'ROLE_ASSIGNED',
  Skipped = 'SKIPPED',
  Failed = 'FAILED',
}

export interface BulkGrantResultItem {
  email: string
  action: BulkGrantAction
  success: boolean
  /** Why the item was skipped or failed. Absent on a plain success. */
  reason?: string
}

export interface BulkGrantAccessResponseDto {
  results: BulkGrantResultItem[]
  invitedCount: number
  resentCount: number
  roleAssignedCount: number
  skippedCount: number
  failureCount: number
}

/**
 * One recipient of a company-scoped revoke: takes a single company away rather
 * than the whole funding role. Used when a billing profile stops listing an
 * address, where the person may still receive invoices for other companies.
 */
export interface RevokeCompanyAccessItemDto {
  email: string
  /** Hint only; the server verifies it against this email before removing. */
  personId: string | null
  /** The company to take away. */
  clientPublicKey: string
  /**
   * Roles to take away as well, applied only when the company removed was the
   * person's last one.
   */
  roleIds: string[]
}

export interface RevokeCompanyAccessRequestDto {
  revocations: RevokeCompanyAccessItemDto[]
}

export enum RevokeCompanyAccessAction {
  CompanyRemoved = 'COMPANY_REMOVED',
  Skipped = 'SKIPPED',
  Failed = 'FAILED',
}

export interface RevokeCompanyAccessResultItem {
  email: string
  action: RevokeCompanyAccessAction
  success: boolean
  /** True when that was their last company, so the funding roles came off too. */
  rolesRemoved: boolean
  /** Why the item was skipped or failed. Absent on a plain success. */
  reason?: string
}

export interface RevokeCompanyAccessResponseDto {
  results: RevokeCompanyAccessResultItem[]
  removedCount: number
  skippedCount: number
  failureCount: number
}

/**
 * One person to take funding access away from entirely. Unlike the
 * company-scoped and role-scoped revokes, this is the whole teardown: the
 * invitation, the invoices, the companies and the roles.
 */
export interface RemoveFundingAccessItemDto {
  email: string
  /** Hint only; the server verifies it against this email before removing. */
  personId: string | null
  roleIds: string[]
}

export interface RemoveFundingAccessRequestDto {
  removals: RemoveFundingAccessItemDto[]
  /** Operator's justification, recorded on the audit trail. */
  reason?: string
}

export enum RemoveFundingAccessAction {
  Removed = 'REMOVED',
  Skipped = 'SKIPPED',
  Failed = 'FAILED',
}

/**
 * What each step actually did, rather than one flag for the lot. A removal can
 * legitimately do some of these and not others: an invitee holds no roles, and
 * a recipient who never registered has no account to take one from.
 */
export interface RemoveFundingAccessResultItem {
  email: string
  action: RemoveFundingAccessAction
  /** A live activation link was withdrawn. */
  invitationRevoked: boolean
  /** Funding roles came off the account. */
  rolesRemoved: boolean
  /** Billing profiles they no longer receive invoices for. */
  profilesRemoved: number
  /** Why nothing happened, or what went wrong. Absent on a plain success. */
  reason?: string
}

export interface RemoveFundingAccessResponseDto {
  results: RemoveFundingAccessResultItem[]
  removedCount: number
  skippedCount: number
  failureCount: number
}

export interface CustomerOption {
  id: string
  name: string
}

export interface UserInvitationHistoryEntry {
  id: number
  eventType: InvitationAuditEvent
  adminUserId: string | null
  metadata: Record<string, unknown> | null
  createdAt: string
}

export interface UserInvitationHistoryResponseDto {
  data: UserInvitationHistoryEntry[]
}
